import React, { useState } from 'react';
import { Mail, Globe, MapPin, Send, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';

// Secure RFC 5322 compliant email validator to protect against malformed inputs or header injection patterns
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Trim input and enforce maximum length sanity limit to mitigate client-side DOS memory abuse
    const cleanEmail = email.trim();
    if (!cleanEmail || cleanEmail.length > 100 || !EMAIL_REGEX.test(cleanEmail)) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    setTimeout(() => {
      try {
        const currentEmails = JSON.parse(localStorage.getItem('ringtail_newsletter_emails') || '[]');
        if (!currentEmails.includes(cleanEmail)) {
          // Keep storage size bounded (max 50 emails stored for demo tracking to avoid local DOM quota exhaustion)
          if (currentEmails.length < 50) {
            currentEmails.push(cleanEmail);
            localStorage.setItem('ringtail_newsletter_emails', JSON.stringify(currentEmails));
          }
        }
        setStatus('success');
        setEmail('');
      } catch (err) {
        setStatus('error');
      }
    }, 1200);
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = contactEmail.trim();
    const cleanMessage = contactMessage.trim();
    
    // Bounds check to defend client states and downstream database payload expectations
    if (
      !cleanEmail || 
      cleanEmail.length > 100 || 
      !EMAIL_REGEX.test(cleanEmail) || 
      !cleanMessage || 
      cleanMessage.length > 2000
    ) {
      setContactStatus('error');
      return;
    }
    setContactStatus('loading');
    setTimeout(() => {
      setContactStatus('success');
      setContactEmail('');
      setContactMessage('');
    }, 1200);
  };

  return (
    <footer id="contact" className="bg-slate-950 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-12">
          <div className="space-y-8 xl:col-span-1">
             <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 overflow-hidden rounded-lg shadow-sm">
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img src="/logo.png" alt="RingTail Press Logo" className="h-full w-full object-cover" />
              </picture>
              </div>
              <span className="font-serif text-xl font-semibold tracking-tight text-white">
                RingTail Press
              </span>
            </div>
            <p className="text-sm leading-6 text-slate-300 w-[230px] max-w-[230px] max-xl:w-full">
              Bridging the gap between traditional publishing and modern technology. Creating beautiful stories and intuitive applications for everyone.
            </p>
            <div className="flex space-x-6">
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <span className="sr-only">GitHub</span>
                 <svg fill="currentColor" viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path></svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <span className="sr-only">X / Twitter</span>
                <svg fill="currentColor" viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path d="M13.6823 10.6218L20.2391 3H18.6854L12.9921 9.61788L8.44486 3H3.2002L10.0765 13.0074L3.2002 21H4.75404L10.7663 14.0113L15.5685 21H20.8131L13.6819 10.6218H13.6823ZM11.5541 13.0956L10.8574 12.0991L5.31391 4.16971H7.70053L12.1511 10.5381L12.8478 11.5346L18.6861 19.8665H16.2995L11.5541 13.096V13.0956Z"></path></svg>
              </a>
            </div>
          </div>
          <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 xl:col-span-2 xl:mt-0">
            <div>
              <h2 className="text-sm font-semibold leading-6 text-white uppercase tracking-wider">Domains & Legal</h2>
              <ul role="list" className="mt-6 space-y-4">
                <li>
                  <a href="https://ringtailpress.com" target="_blank" rel="noopener noreferrer" className="text-sm leading-6 text-slate-300 hover:text-white flex items-center gap-2 transition-colors">
                    <Globe size={14} /> ringtailpress.com
                  </a>
                </li>
                <li>
                  <a href="https://ringtailpress.co.uk" target="_blank" rel="noopener noreferrer" className="text-sm leading-6 text-slate-300 hover:text-white flex items-center gap-2 transition-colors">
                    <Globe size={14} /> ringtailpress.co.uk
                  </a>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm leading-6 text-slate-300 hover:text-white mt-4 block transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-sm leading-6 text-slate-300 hover:text-white transition-colors">
                    Terms & Conditions
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-sm font-semibold leading-6 text-white uppercase tracking-wider">Support</h2>
              <ul role="list" className="mt-6 mb-6 space-y-4">
                <li className="text-sm leading-6 text-slate-300 flex items-start gap-2">
                   <MapPin size={14} className="mt-1 flex-shrink-0" />
                   <span className="text-sm">
                     RingTail Press,<br />
                     Suite RA01, 195-197 Wood Street,<br />
                     London, E17 3NU
                   </span>
                </li>
              </ul>
              {contactStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl bg-emerald-500/10 p-4 border border-emerald-500/20"
                >
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium mb-1">
                    <CheckCircle2 size={16} />
                    Message Sent
                  </div>
                  <p className="text-xs text-slate-400">We'll get back to you shortly.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleContactSubmit} className="flex flex-col gap-2">
                  <input
                    type="email"
                    required
                    maxLength={100}
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      if (contactStatus === 'error') setContactStatus('idle');
                    }}
                    placeholder="Your email address"
                    className="w-full bg-[#FAF9F6]/5 text-[#FAF9F6] border border-white/10 rounded-xl px-4 py-2 text-sm outline-none focus:border-white/30 transition-all placeholder:text-slate-500"
                    disabled={contactStatus === 'loading'}
                  />
                  <textarea
                    required
                    maxLength={2000}
                    value={contactMessage}
                    onChange={(e) => {
                      setContactMessage(e.target.value);
                      if (contactStatus === 'error') setContactStatus('idle');
                    }}
                    placeholder="How can we help?"
                    rows={2}
                    className="w-full bg-[#FAF9F6]/5 text-[#FAF9F6] border border-white/10 rounded-xl px-4 py-2 text-sm outline-none focus:border-white/30 transition-all placeholder:text-slate-500 resize-none"
                    disabled={contactStatus === 'loading'}
                  />
                  <button
                    type="submit"
                    disabled={contactStatus === 'loading'}
                    aria-label={contactStatus === 'loading' ? 'Sending message...' : 'Send Message'}
                    className="flex justify-center items-center gap-2 w-full rounded-xl bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20 transition-all disabled:opacity-50"
                  >
                    {contactStatus === 'loading' ? (
                      <span className="block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent border-white" />
                    ) : (
                      <>
                        <Send size={14} /> Send Message
                      </>
                    )}
                  </button>
                  <AnimatePresence mode="wait">
                    {contactStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="text-rose-400 text-xs mt-1"
                      >
                        Please provide a valid email and message.
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              )}
            </div>
            <div className="sm:col-span-2 md:col-span-1">
              <h3 className="text-sm font-semibold leading-6 text-white uppercase tracking-wider">Newsletter</h3>
              <p className="mt-6 text-sm text-slate-400 leading-6">
                Be the first to hear about new book releases, update announcements, and creative insights.
              </p>
              <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-2">
                <div className="relative flex items-center">
                  <input
                    type="email"
                    required
                    maxLength={100}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === 'error') setStatus('idle');
                    }}
                    placeholder="Enter your email"
                    className="w-full bg-[#FAF9F6]/5 text-[#FAF9F6] border border-white/10 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-white/30 transition-all placeholder:text-slate-500 pr-10"
                    disabled={status === 'loading' || status === 'success'}
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading' || status === 'success'}
                    aria-label={status === 'loading' ? 'Subscribing...' : 'Subscribe to newsletter'}
                    className="absolute right-1 text-slate-400 hover:text-[#FAF9F6] p-1.5 transition-colors disabled:opacity-50"
                  >
                    {status === 'loading' ? (
                      <span className="block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent border-slate-400" />
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
                <AnimatePresence mode="wait">
                  {status === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="flex items-center gap-2 text-emerald-400 text-xs mt-1"
                    >
                      <CheckCircle2 size={12} />
                      <span>Thank you! You've been subscribed.</span>
                    </motion.div>
                  )}
                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="text-rose-400 text-xs mt-1"
                    >
                      <span>Please enter a valid email address.</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </form>
            </div>
          </div>
        </div>
        <div className="mt-16 border-t border-white/10 pt-8 sm:mt-20 lg:mt-24">
          <p className="text-xs leading-5 text-slate-500">&copy; {new Date().getFullYear()} RingTail Press. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
